import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { repository } from "@/data"
import { DomainError } from "@/data/repository"
import { isConcurrencyConflict, retryWithLatestVersion } from "@/lib/merge-on-conflict"
import { withUploadingToast } from "@/lib/upload-toast"
import type {
  CalendarReprint,
  BlockItemInput,
  CatalogFilters,
  CatalogCollectionInput,
  CatalogItemDocument,
  CatalogItemMockupAsset,
  CatalogItemInput,
  CatalogProductImage,
  CatalogPublicMediaSelection,
  CatalogImageAltText,
  SaveCatalogPublicMediaOptions,
  CreateKitTemplateInput,
  CreatePipelineTemplateInput,
  CreateCatalogProductInput,
  CreateProductDriveFoldersInput,
  DeleteCatalogItemDocumentInput,
  DeleteCatalogItemMockupAssetInput,
  DeleteCatalogProductImageInput,
  ImprintInput,
  InviteUserInput,
  ItemTypeInput,
  ItemTypeVariantInput,
  PipelineVersionContentInput,
  SavePipelineStageEstimatesInput,
  PrintShopInput,
  ProductionFilters,
  SaveCatalogProductInput,
  SetProductionLeadInput,
  SetProductionStageAssignmentInput,
  SaveProductionScheduleInput,
  SetUserTeamInput,
  UpdateUserInput,
  EditorialFunction,
  StartProductionInput,
  SyncProductionWithDriveInput,
  UpdateProductionInput,
  UpdateProductionQuantitiesInput,
  UploadToDriveStageInput,
  UploadSupplementaryFileInput,
  SupplementaryFileKind,
  TransitionItemInput,
  SkipProductionItemInput,
  ReturnProductionItemInput,
  UploadCatalogItemDocumentInput,
  UploadCatalogItemMockupAssetInput,
  UploadCatalogProductImageInput,
  WorkspaceUser,
  ImprintLogoTarget,
  UploadImprintLogoInput,
  ProductionPrintDelivery,
  ProductionPrintItem,
} from "@/types/domain"
import type { PrintChecklistKey } from "@/lib/print-checklist"
import { sortImprints } from "@/lib/imprint-kind"

export const queryKeys = {
  dashboard: ["dashboard"] as const,
  notifications: ["notifications"] as const,
  templates: ["templates"] as const,
  users: ["users"] as const,
  assignableUsers: ["assignable-users"] as const,
  myTasks: ["my-tasks"] as const,
  imprints: ["imprints"] as const,
  catalogCollections: ["catalog-collections"] as const,
  printShops: ["print-shops"] as const,
  catalogProducts: (filters: CatalogFilters = {}) => ["catalog-products", filters] as const,
  catalogPublicationYears: ["catalog-publication-years"] as const,
  catalogSearch: (query: string, filters: CatalogFilters = {}) => ["catalog-search", query, filters] as const,
  catalogProduct: (id: string) => ["catalog-product", id] as const,
  catalogItem: (id: string) => ["catalog-item", id] as const,
  catalogItemActivity: (id: string) => ["catalog-item-activity", id] as const,
  productionRuns: (filters: ProductionFilters = {}) => ["production-runs", filters] as const,
  // Sob o prefixo "production-runs", para ser atualizado junto com a lista depois de qualquer alteração na produção.
  productionPrintItems: (runIds: string[]) => ["production-runs", "print-items", [...runIds].sort()] as const,
  productionRun: (id: string) => ["production-run", id] as const,
  itemTypeCatalog: ["item-type-catalog"] as const,
  kitPresets: ["kit-presets"] as const,
  pipelineCatalog: ["pipeline-catalog"] as const,
  pipelineStageEstimates: (pipelineTemplateId: string) => ["pipeline-stage-estimates", pipelineTemplateId] as const,
  googleDriveConnection: ["google-drive-connection"] as const,
  driveItemStageFolders: (runId: string, itemId: string) => ["drive-item-stage-folders", runId, itemId] as const,
}

export function useCatalogProductsQuery(filters: CatalogFilters = {}) {
  return useQuery({ queryKey: queryKeys.catalogProducts(filters), queryFn: () => repository.listCatalogProducts(filters).then((page) => page.items) })
}

export function useCatalogProductsInfiniteQuery(filters: CatalogFilters = {}, enabled = true) {
  return useInfiniteQuery({
    queryKey: [...queryKeys.catalogProducts(filters), "infinite"],
    queryFn: ({ pageParam }) => repository.listCatalogProducts(filters, { cursor: pageParam ?? undefined, limit: 50 }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled,
  })
}

export function useCatalogProductsForPeriodQuery(filters: CatalogFilters, months: Array<{ month: number; year: number }>, enabled = true) {
  return useQuery({
    queryKey: [...queryKeys.catalogProducts(filters), "period", months.map(({ month, year }) => `${year}-${month}`)],
    queryFn: async () => {
      const monthItems = await Promise.all(months.map(async ({ month, year }) => {
        const items = []
        let cursor: string | undefined
        do {
          const page = await repository.listCatalogProducts(
            { ...filters, publicationMonth: month, publicationYear: year },
            { cursor, limit: 100 },
          )
          items.push(...page.items)
          cursor = page.nextCursor
        } while (cursor)
        return items
      }))
      return monthItems.flat()
    },
    enabled,
  })
}

export function useCatalogPublicationYearsQuery() {
  return useQuery({ queryKey: queryKeys.catalogPublicationYears, queryFn: () => repository.listCatalogPublicationYears() })
}

export function useCatalogCollectionsQuery() {
  return useQuery({ queryKey: queryKeys.catalogCollections, queryFn: () => repository.listCatalogCollections() })
}

function useCatalogCollectionMutation<T, TResult = void>(mutationFn: (input: T) => Promise<TResult>) {
  const client = useQueryClient()
  return useMutation({
    mutationFn,
    onSuccess: async () => {
      // Mark individual runs stale before the calendar aggregate can refetch them.
      await client.invalidateQueries({ queryKey: ["production-run"] })
      await Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.catalogCollections }),
        client.invalidateQueries({ queryKey: ["catalog-products"] }),
        client.invalidateQueries({ queryKey: ["catalog-search"] }),
        client.invalidateQueries({ queryKey: ["catalog-product"] }),
      ])
    },
  })
}

export function useCreateCatalogCollection() {
  return useCatalogCollectionMutation<CatalogCollectionInput, string>((input) => repository.createCatalogCollection(input))
}

export function useUpdateCatalogCollection() {
  return useCatalogCollectionMutation<{ collectionId: string; input: CatalogCollectionInput; expectedVersion: number }>((payload) => repository.updateCatalogCollection(payload.collectionId, payload.input, payload.expectedVersion))
}

export function useSetCatalogCollectionActive() {
  return useCatalogCollectionMutation<{ collectionId: string; active: boolean; reason: string; expectedVersion: number }>((payload) => repository.setCatalogCollectionActive(payload.collectionId, payload.active, payload.reason, payload.expectedVersion))
}

export function useDeleteCatalogCollection() {
  return useCatalogCollectionMutation<string>((collectionId) => repository.deleteCatalogCollection(collectionId))
}

export function useCatalogSearchQuery(query: string, filters: CatalogFilters = {}) {
  return useQuery({ queryKey: queryKeys.catalogSearch(query, filters), queryFn: () => repository.searchCatalog(query, filters).then((page) => page.items) })
}

export function useCatalogSearchInfiniteQuery(query: string, filters: CatalogFilters = {}, enabled = true) {
  return useInfiniteQuery({
    queryKey: [...queryKeys.catalogSearch(query, filters), "infinite"],
    queryFn: ({ pageParam }) => repository.searchCatalog(query, filters, { cursor: pageParam ?? undefined, limit: 50 }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled,
  })
}

export function useCatalogProductQuery(productId: string) {
  return useQuery({ queryKey: queryKeys.catalogProduct(productId), queryFn: () => repository.getCatalogProduct(productId), enabled: Boolean(productId) })
}

/**
 * Versões atuais lidas direto do repositório, sem atualizar o cache: usadas para mesclar com o que outra pessoa
 * salvou sem recriar a tela (o que descartaria o rascunho).
 */
export const fetchLatest = {
  catalogItem: (itemId: string) => repository.getCatalogItem(itemId),
  catalogProduct: (productId: string) => repository.getCatalogProduct(productId),
  catalogCollection: async (collectionId: string) => found((await repository.listCatalogCollections()).find((collection) => collection.id === collectionId)),
  itemType: async (itemTypeId: string) => found((await repository.getItemTypeCatalog()).find((itemType) => itemType.id === itemTypeId)),
  imprint: async (imprintId: string) => found((await repository.listImprints()).find((imprint) => imprint.id === imprintId)),
  printShop: async (printShopId: string) => found((await repository.listPrintShops()).find((printShop) => printShop.id === printShopId)),
  productionRun: (runId: string) => repository.getProductionRun(runId),
  templateCatalog: () => repository.getTemplateCatalog(),
  pipelineStageEstimates: (pipelineTemplateId: string) => repository.listPipelineStageEstimates(pipelineTemplateId),
  user: async (userId: string) => found((await repository.listUsers()).find((user) => user.id === userId)),
}

function found<T>(value: T | undefined): T {
  if (value === undefined) throw new DomainError("NOT_FOUND", "Este registro foi apagado por outra pessoa.")
  return value
}

export function useCatalogItemQuery(itemId: string) {
  return useQuery({ queryKey: queryKeys.catalogItem(itemId), queryFn: () => repository.getCatalogItem(itemId), enabled: Boolean(itemId) })
}

export function useCatalogItemActivityQuery(itemId: string) {
  return useQuery({ queryKey: queryKeys.catalogItemActivity(itemId), queryFn: () => repository.listCatalogItemActivity(itemId), enabled: Boolean(itemId) })
}

const catalogItemActivityPageSize = 10

/** Todas as atualizações do item, carregadas de 10 em 10. */
export function useCatalogItemActivityPagesQuery(itemId: string, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: [...queryKeys.catalogItemActivity(itemId), "pages"],
    queryFn: ({ pageParam }) => repository.listCatalogItemActivity(itemId, catalogItemActivityPageSize, pageParam),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => lastPage.length === catalogItemActivityPageSize ? pages.length * catalogItemActivityPageSize : undefined,
    enabled: Boolean(itemId) && enabled,
  })
}

export function useCatalogItemEditionSnapshot() {
  return useMutation({ mutationFn: (snapshotId: string) => repository.getCatalogItemEditionSnapshot(snapshotId) })
}

export function useProductionRunsQuery(filters: ProductionFilters = {}, enabled = true) {
  return useQuery({ queryKey: queryKeys.productionRuns(filters), queryFn: () => repository.listProductionRuns(filters), enabled })
}

export function useProductionPrintItemsQuery(runIds: string[], enabled = true) {
  return useQuery<ProductionPrintItem[]>({ queryKey: queryKeys.productionPrintItems(runIds), queryFn: () => repository.listProductionPrintItems(runIds), enabled })
}

export function useProductionRunsForPeriodQuery(months: Array<{ month: number; year: number }>, enabled = true) {
  return useQuery({
    queryKey: [...queryKeys.productionRuns(), "period", months.map(({ month, year }) => `${year}-${month}`)],
    queryFn: async () => {
      const runs = await Promise.all(months.map(({ month, year }) => repository.listProductionRuns({ month, year })))
      return [...new Map(runs.flat().map((run) => [run.id, run])).values()]
    },
    enabled,
  })
}

/** Reimpressões com competência no período, com o produto de cada uma; no calendário, aparecem no mês da competência. */
export function useCalendarReprintsQuery(months: Array<{ month: number; year: number }>, search?: string, enabled = true) {
  const queryClient = useQueryClient()
  return useQuery({
    queryKey: [...queryKeys.productionRuns({ reprint: "ONLY", search }), "period", months.map(({ month, year }) => `${year}-${month}`)],
    queryFn: async (): Promise<CalendarReprint[]> => {
      const pages = await Promise.all(months.map(({ month, year }) => repository.listProductionRuns({ month, year, search, reprint: "ONLY" })))
      const runs = [...new Map(pages.flat().map((run) => [run.id, run])).values()]
      const products = new Map((await Promise.all([...new Set(runs.map((run) => run.product.id))].map((productId) => queryClient.fetchQuery({
        queryKey: queryKeys.catalogProduct(productId),
        queryFn: () => repository.getCatalogProduct(productId),
        staleTime: 30_000,
      })))).map((product) => [product.id, product]))
      return runs.flatMap((run) => {
        const product = products.get(run.product.id)
        return product ? [{ run, product }] : []
      })
    },
    enabled,
  })
}

export function useProductionRunQuery(runId: string) {
  return useQuery({ queryKey: queryKeys.productionRun(runId), queryFn: () => repository.getProductionRun(runId), enabled: Boolean(runId) })
}

export function useCalendarProductionDetailsQuery(runIds: string[]) {
  const queryClient = useQueryClient()
  const ids = [...new Set(runIds.filter(Boolean))].sort()

  return useQuery({
    queryKey: ["production-runs", "calendar-details", ids] as const,
    queryFn: async ({ signal }) => {
      const runs: Array<Awaited<ReturnType<typeof repository.getProductionRun>> | undefined> = Array(ids.length)
      let nextIndex = 0
      const worker = async () => {
        while (nextIndex < ids.length) {
          signal.throwIfAborted()
          const index = nextIndex
          nextIndex += 1
          const id = ids[index]
          runs[index] = await queryClient.fetchQuery({
            queryKey: queryKeys.productionRun(id),
            queryFn: () => repository.getProductionRun(id),
            staleTime: 30_000,
          })
        }
      }

      await Promise.all(Array.from({ length: Math.min(4, ids.length) }, () => worker()))
      return runs as Awaited<ReturnType<typeof repository.getProductionRun>>[]
    },
    enabled: ids.length > 0,
    staleTime: 30_000,
  })
}

export function useItemTypeCatalogQuery() {
  return useQuery({ queryKey: queryKeys.itemTypeCatalog, queryFn: () => repository.getItemTypeCatalog() })
}

export function useKitPresetsQuery() {
  return useQuery({ queryKey: queryKeys.kitPresets, queryFn: () => repository.getKitPresets() })
}

export function usePipelineCatalogQuery() {
  return useQuery({ queryKey: queryKeys.pipelineCatalog, queryFn: () => repository.getPipelineCatalog() })
}

function useCatalogMutation<T, TResult>(mutationFn: (input: T) => Promise<TResult>, { refreshOnConflict = false } = {}) {
  const client = useQueryClient()
  return useMutation({
    mutationFn,
    // Ações que não se repetem sozinhas (avançar etapa, marcar checklist…): em conflito, a tela é atualizada para a
    // pessoa conferir antes de tentar de novo.
    onError: refreshOnConflict ? async (cause) => {
      if (isConcurrencyConflict(cause)) await client.invalidateQueries({ queryKey: ["production-run"] })
    } : undefined,
    onSuccess: async () => {
      // Refresh details before calendar aggregates can reuse their cached values.
      await client.invalidateQueries({ queryKey: ["production-run"] })
      await Promise.all([
        client.invalidateQueries({ queryKey: ["catalog-products"] }),
        client.invalidateQueries({ queryKey: queryKeys.catalogPublicationYears }),
        client.invalidateQueries({ queryKey: ["catalog-search"] }),
        client.invalidateQueries({ queryKey: ["catalog-product"] }),
        client.invalidateQueries({ queryKey: ["catalog-item"] }),
        client.invalidateQueries({ queryKey: ["catalog-item-activity"] }),
        client.invalidateQueries({ queryKey: ["production-runs"] }),
        client.invalidateQueries({ queryKey: queryKeys.myTasks }),
        client.invalidateQueries({ queryKey: queryKeys.dashboard }),
        client.invalidateQueries({ queryKey: queryKeys.notifications }),
        client.invalidateQueries({ queryKey: queryKeys.printShops }),
      ])
    },
  })
}

export function useCreateCatalogProduct() {
  return useCatalogMutation<CreateCatalogProductInput, string>((input) => repository.createCatalogProduct(input))
}

export function useSaveCatalogProduct() {
  return useCatalogMutation<{ productId: string; input: SaveCatalogProductInput; expectedVersion: number; confirmActiveProduction?: boolean }, Awaited<ReturnType<typeof repository.saveCatalogProduct>>>((payload) =>
    repository.saveCatalogProduct(payload.productId, payload.input, payload.expectedVersion, payload.confirmActiveProduction),
  )
}

export function useUploadSupplementaryFile() {
  return useMutation({ mutationFn: (input: UploadSupplementaryFileInput) => withUploadingToast(input.file.name, () => repository.uploadSupplementaryFile(input)) })
}

export function useTrashSupplementaryFile() {
  return useMutation({ mutationFn: ({ itemId, kind }: { itemId: string; kind: SupplementaryFileKind }) => repository.trashSupplementaryFile(itemId, kind) })
}

export function useExtractCredits() {
  return useMutation({ mutationFn: (fileUrl: string) => repository.extractCreditsFromDrive(fileUrl) })
}

export function useExtractFao() {
  return useMutation({ mutationFn: (fileUrl: string) => repository.extractFaoFromDrive(fileUrl) })
}

export function useExtractCatalogRecord() {
  return useMutation({ mutationFn: (fileUrl: string) => repository.extractCatalogRecordFromDrive(fileUrl) })
}

export function useSaveCatalogItem() {
  return useCatalogMutation<{ itemId: string; input: CatalogItemInput; expectedVersion: number; confirmActiveProduction?: boolean }, Awaited<ReturnType<typeof repository.saveCatalogItem>>>((payload) =>
    repository.saveCatalogItem(payload.itemId, payload.input, payload.expectedVersion, payload.confirmActiveProduction),
  )
}

export function useCreateCatalogItemEdition() {
  return useCatalogMutation<{ itemId: string; expectedVersion: number; confirmActiveProduction?: boolean }, Awaited<ReturnType<typeof repository.createCatalogItemEdition>>>((payload) =>
    repository.createCatalogItemEdition(payload.itemId, payload.expectedVersion, payload.confirmActiveProduction),
  )
}

export function useDeleteCatalogProduct() {
  return useCatalogMutation<{ productId: string; expectedVersion: number }, Awaited<ReturnType<typeof repository.deleteCatalogProduct>>>((payload) =>
    repository.deleteCatalogProduct(payload.productId, payload.expectedVersion),
  )
}

export function useStartProduction() {
  return useCatalogMutation<StartProductionInput, string>((input) => repository.startProduction(input))
}

function useProductionMutation<T>(mutationFn: (input: T) => ReturnType<typeof repository.getProductionRun>) {
  return useCatalogMutation<T, Awaited<ReturnType<typeof repository.getProductionRun>>>(mutationFn, { refreshOnConflict: true })
}

export function useCancelProduction() {
  return useProductionMutation<{ runId: string; expectedVersion: number; reason: string }>((payload) => repository.cancelProduction(payload.runId, payload.expectedVersion, payload.reason))
}

export function useUpdateProduction() {
  return useProductionMutation<UpdateProductionInput>((input) => repository.updateProduction(input))
}

export function useUpdateProductionQuantities() {
  return useProductionMutation<UpdateProductionQuantitiesInput>((input) => repository.updateProductionQuantities(input))
}

export function useStopProduction() {
  return useProductionMutation<{ runId: string; expectedVersion: number; reason: string }>((input) => repository.stopProduction(input.runId, input.expectedVersion, input.reason))
}

export function useResumeProduction() {
  return useProductionMutation<{ runId: string; expectedVersion: number }>((input) => repository.resumeProduction(input.runId, input.expectedVersion))
}

export function useDeleteProduction() {
  return useCatalogMutation<{ runId: string; expectedVersion: number }, void>((payload) => repository.deleteProduction(payload.runId, payload.expectedVersion))
}

export function useTransitionProductionItem() { return useProductionMutation<TransitionItemInput>((input) => repository.transitionProductionItem(input)) }
export function useSkipProductionItem() { return useProductionMutation<SkipProductionItemInput>((input) => repository.skipProductionItem(input)) }
export function useReturnProductionItem() { return useProductionMutation<ReturnProductionItemInput>((input) => repository.returnProductionItem(input)) }
export function useBlockProductionItem() { return useProductionMutation<BlockItemInput>((input) => repository.blockProductionItem(input)) }
export function useUnblockProductionItem() { return useProductionMutation<Omit<BlockItemInput, "reason">>((input) => repository.unblockProductionItem(input)) }
export function useToggleProductionChecklist() { return useProductionMutation<{ checklistId: string; expectedVersion: number }>((input) => repository.toggleProductionChecklist(input.checklistId, input.expectedVersion)) }
export function useToggleProductionGate() { return useProductionMutation<{ gateId: string; expectedVersion: number }>((input) => repository.toggleProductionGate(input.gateId, input.expectedVersion)) }
export function useSetProductionItemPrintDeliveries() { return useProductionMutation<{ itemId: string; deliveries: ProductionPrintDelivery[]; expectedVersion: number }>((input) => repository.setProductionItemPrintDeliveries(input.itemId, input.deliveries, input.expectedVersion)) }
export function useSetProductionItemPrintChecklist() { return useProductionMutation<{ itemId: string; key: PrintChecklistKey; done: boolean; expectedVersion: number }>((input) => repository.setProductionItemPrintChecklist(input.itemId, input.key, input.done, input.expectedVersion)) }
export function useSetProductionItemQuoteUrl() { return useProductionMutation<{ itemId: string; quoteUrl: string | null; expectedVersion: number }>((input) => repository.setProductionItemQuoteUrl(input.itemId, input.quoteUrl, input.expectedVersion)) }
export function useAssignProductionItemPrintShop() { return useProductionMutation<{ itemId: string; printShopId?: string; expectedVersion: number }>((input) => repository.assignProductionItemPrintShop(input.itemId, input.printShopId, input.expectedVersion)) }

export function useSetProductionLead() { return useProductionMutation<SetProductionLeadInput>((input) => repository.setProductionLead(input)) }
export function useSetProductionStageAssignment() { return useProductionMutation<SetProductionStageAssignmentInput>((input) => repository.setProductionStageAssignment(input)) }
export function useSaveProductionSchedule() { return useProductionMutation<SaveProductionScheduleInput>((input) => repository.saveProductionSchedule(input)) }

export function useAssignableUsersQuery(enabled = true) {
  return useQuery({ queryKey: queryKeys.assignableUsers, queryFn: () => repository.listAssignableUsers(), enabled })
}

export function useMyTasksQuery() {
  return useQuery({ queryKey: queryKeys.myTasks, queryFn: () => repository.listMyTasks() })
}

export function useFollowProduction() {
  const client = useQueryClient()
  return useMutation({ mutationFn: ({ runId, follow }: { runId: string; follow: boolean }) => repository.followProduction(runId, follow), onSuccess: () => client.invalidateQueries({ queryKey: ["production-run"] }) })
}

export function useUploadCatalogItemDocument() {
  return useCatalogMutation<UploadCatalogItemDocumentInput, Awaited<ReturnType<typeof repository.uploadCatalogItemDocument>>>((input) => withUploadingToast(input.file.name, () => repository.uploadCatalogItemDocument(input)))
}

export function useDeleteCatalogItemDocument() {
  return useCatalogMutation<DeleteCatalogItemDocumentInput, Awaited<ReturnType<typeof repository.deleteCatalogItemDocument>>>((input) => repository.deleteCatalogItemDocument(input))
}

export function useCatalogItemDocumentUrl() {
  return useMutation({ mutationFn: (document: CatalogItemDocument) => repository.getCatalogItemDocumentUrl(document) })
}

export function useUploadCatalogItemMockupAsset() {
  return useCatalogMutation<UploadCatalogItemMockupAssetInput, Awaited<ReturnType<typeof repository.uploadCatalogItemMockupAsset>>>((input) => withUploadingToast(input.file.name, () => repository.uploadCatalogItemMockupAsset(input)))
}

export function useDeleteCatalogItemMockupAsset() {
  return useCatalogMutation<DeleteCatalogItemMockupAssetInput, Awaited<ReturnType<typeof repository.deleteCatalogItemMockupAsset>>>((input) => repository.deleteCatalogItemMockupAsset(input))
}

export function useCatalogItemMockupAssetUrl() {
  return useMutation({ mutationFn: (asset: CatalogItemMockupAsset) => repository.getCatalogItemMockupAssetUrl(asset) })
}

export function useUploadCatalogProductImage() {
  return useCatalogMutation<UploadCatalogProductImageInput, Awaited<ReturnType<typeof repository.uploadCatalogProductImage>>>((input) => withUploadingToast(input.file.name, () => repository.uploadCatalogProductImage(input)))
}

export function useDeleteCatalogProductImage() {
  return useCatalogMutation<DeleteCatalogProductImageInput, Awaited<ReturnType<typeof repository.deleteCatalogProductImage>>>((input) => repository.deleteCatalogProductImage(input))
}

export function useMockupStudioKit() {
  return useMutation({ mutationFn: (productId: string) => repository.getMockupStudioKit(productId) })
}

// Envia tudo antes e registra numa só gravação: um conflito de versão repete só o registro, sem reenviar.
export function useSaveStudioKitImages() {
  return useCatalogMutation(async ({ productId, files, version, onProgress }: { productId: string; files: File[]; version: number; onProgress?: (uploaded: number) => void }) => {
    const staged = await repository.stageCatalogProductImages(productId, files, onProgress)
    try {
      return await retryWithLatestVersion(version, (expectedVersion) => repository.replaceStudioKitImages(productId, staged, expectedVersion), async () => (await fetchLatest.catalogProduct(productId)).version)
    } catch (cause) {
      await repository.discardStagedCatalogProductImages(staged)
      throw cause
    }
  })
}

// Uma mutação para o lote todo: a tela só é atualizada no fim, e não a cada imagem.
export function useDeleteCatalogProductImages() {
  return useCatalogMutation(async ({ productId, imageIds, version, onProgress }: { productId: string; imageIds: string[]; version: number; onProgress?: (deleted: number) => void }) => {
    let current = version
    let storageCleanupFailed = false
    for (const [index, imageId] of imageIds.entries()) {
      try {
        const result = await retryWithLatestVersion(current, (expectedVersion) => repository.deleteCatalogProductImage({ productId, imageId, expectedVersion }), async () => (await fetchLatest.catalogProduct(productId)).version)
        current = result.product.version
        storageCleanupFailed ||= result.storageCleanupFailed
      } catch (cause) {
        if (!index) throw cause
        const message = cause instanceof Error ? cause.message : "Tente novamente."
        throw new DomainError(cause instanceof DomainError ? cause.code : "PARTIAL_DELETE", `${index} de ${imageIds.length} imagens foram excluídas. ${message}`)
      }
      onProgress?.(index + 1)
    }
    return { deletedCount: imageIds.length, storageCleanupFailed }
  })
}

export function useCatalogProductImageUrl() {
  return useMutation({ mutationFn: (image: CatalogProductImage) => repository.getCatalogProductImageUrl(image) })
}

export function useCatalogProductImagePreviewQuery(image: CatalogProductImage) {
  // Signed URLs live for an hour; keep the preview well inside that window.
  return useQuery({ queryKey: ["catalog-product-image-preview", image.storagePath], queryFn: () => repository.getCatalogProductImagePreview(image), staleTime: 30 * 60 * 1000, gcTime: 30 * 60 * 1000 })
}

export function useSaveCatalogPublicMedia() {
  return useCatalogMutation<{ productId: string; media: CatalogPublicMediaSelection[]; expectedVersion: number; altTexts?: CatalogImageAltText[]; options?: SaveCatalogPublicMediaOptions }, Awaited<ReturnType<typeof repository.saveCatalogPublicMedia>>>((input) =>
    repository.saveCatalogPublicMedia(input.productId, input.media, input.expectedVersion, input.altTexts, input.options),
  )
}

export function usePublishCatalogProduct() {
  return useCatalogMutation<{ productId: string; publicSlug: string; expectedVersion: number }, Awaited<ReturnType<typeof repository.publishCatalogProduct>>>((input) =>
    repository.publishCatalogProduct(input.productId, input.publicSlug, input.expectedVersion),
  )
}

export function useUnpublishCatalogProduct() {
  return useCatalogMutation<{ productId: string; expectedVersion: number }, Awaited<ReturnType<typeof repository.unpublishCatalogProduct>>>((input) =>
    repository.unpublishCatalogProduct(input.productId, input.expectedVersion),
  )
}

export function useDashboardQuery() {
  return useQuery({ queryKey: queryKeys.dashboard, queryFn: () => repository.getDashboard() })
}

export function useNotificationsQuery() {
  return useQuery({ queryKey: queryKeys.notifications, queryFn: () => repository.listNotifications() })
}

export function useMarkNotificationRead() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => repository.markNotificationRead(id),
    onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.notifications }),
  })
}

export function useTemplateCatalogQuery() {
  return useQuery({ queryKey: queryKeys.templates, queryFn: () => repository.getTemplateCatalog() })
}

export function useCreateItemType() {
  return useTemplateMutation<ItemTypeInput, string>((input) => repository.createItemType(input))
}

export function useUpdateItemType() {
  return useTemplateMutation<{ itemTypeId: string; name: string }>((input) => repository.updateItemType(input.itemTypeId, input.name))
}

export function useSaveItemType() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ input, itemTypeId, expectedVersion }: { input: ItemTypeInput; itemTypeId?: string; expectedVersion?: number }) => repository.saveItemType(input, itemTypeId, expectedVersion),
    onSuccess: async () => Promise.all([
      client.invalidateQueries({ queryKey: queryKeys.itemTypeCatalog }),
      client.invalidateQueries({ queryKey: queryKeys.templates }),
      client.invalidateQueries({ queryKey: queryKeys.kitPresets }),
      client.invalidateQueries({ queryKey: queryKeys.pipelineCatalog }),
    ]),
  })
}

function useItemTypeCatalogMutation<T, TResult = void>(mutationFn: (input: T) => Promise<TResult>) {
  const client = useQueryClient()
  return useMutation({
    mutationFn,
    onSuccess: async () => Promise.all([
      client.invalidateQueries({ queryKey: queryKeys.itemTypeCatalog }),
      client.invalidateQueries({ queryKey: queryKeys.templates }),
      client.invalidateQueries({ queryKey: queryKeys.kitPresets }),
      client.invalidateQueries({ queryKey: queryKeys.pipelineCatalog }),
    ]),
  })
}

export function useDeleteItemType() {
  return useItemTypeCatalogMutation<{ itemTypeId: string; expectedVersion: number }>((input) => repository.deleteItemType(input.itemTypeId, input.expectedVersion))
}

export function useSaveItemTypeVariant() {
  return useItemTypeCatalogMutation<{ itemTypeId: string; input: ItemTypeVariantInput; variantId?: string; expectedVersion?: number }, string>((payload) => repository.saveItemTypeVariant(payload.itemTypeId, payload.input, payload.variantId, payload.expectedVersion))
}

export function useDeleteItemTypeVariant() {
  return useItemTypeCatalogMutation<{ variantId: string; expectedVersion: number }>((input) => repository.deleteItemTypeVariant(input.variantId, input.expectedVersion))
}

export function useUsersQuery() {
  return useQuery({ queryKey: queryKeys.users, queryFn: () => repository.listUsers() })
}

function useUserMutation<T, TResult = void>(mutationFn: (input: T) => Promise<TResult>) {
  const client = useQueryClient()
  return useMutation({
    mutationFn,
    onSuccess: () => Promise.all([
      client.invalidateQueries({ queryKey: queryKeys.users }),
      client.invalidateQueries({ queryKey: queryKeys.assignableUsers }),
    ]),
  })
}

export function useInviteUser() {
  return useUserMutation<InviteUserInput, string>((input) => repository.inviteUser(input))
}

export function useSetUserRole() {
  return useUserMutation<{ userId: string; role: WorkspaceUser["role"] }>((input) => repository.setUserRole(input.userId, input.role))
}

export function useSetUserTeam() {
  return useUserMutation<SetUserTeamInput>((input) => repository.setUserTeam(input))
}

export function useUpdateUser() {
  return useUserMutation<UpdateUserInput>((input) => repository.updateUser(input))
}

export function useDeleteUser() {
  return useUserMutation<string>((userId) => repository.deleteUser(userId))
}

function useTemplateMutation<T, TResult = void>(mutationFn: (input: T) => Promise<TResult>) {
  const client = useQueryClient()
  return useMutation({
    mutationFn,
    onSuccess: async () => Promise.all([
      client.invalidateQueries({ queryKey: queryKeys.templates }),
      client.invalidateQueries({ queryKey: queryKeys.kitPresets }),
      client.invalidateQueries({ queryKey: queryKeys.pipelineCatalog }),
    ]),
  })
}

export function useCreateKitTemplate() {
  return useTemplateMutation<CreateKitTemplateInput, string>((input) => repository.createKitTemplate(input))
}

export function useUpdateKitTemplate() {
  return useTemplateMutation<{ templateId: string; input: CreateKitTemplateInput; expectedVersion?: number }, string>((payload) =>
    repository.updateKitTemplate(payload.templateId, payload.input, payload.expectedVersion),
  )
}

export function useDeleteKitTemplate() {
  return useTemplateMutation<string>((templateId) => repository.deleteKitTemplate(templateId))
}

export function useCreatePipelineTemplate() {
  return useTemplateMutation<CreatePipelineTemplateInput, string>((input) => repository.createPipelineTemplate(input))
}

export function useUpdatePipelineTemplateVersion() {
  return useTemplateMutation<{ versionId: string; content: PipelineVersionContentInput; expectedVersion?: number }>((input) =>
    repository.updatePipelineTemplateVersion(input.versionId, input.content, input.expectedVersion),
  )
}

export function useSetPipelineStageFunction() {
  return useTemplateMutation<{ stageId: string; editorialFunction: EditorialFunction | null }>((input) => repository.setPipelineStageFunction(input.stageId, input.editorialFunction))
}

export function useCreatePipelineTemplateVersion() {
  return useTemplateMutation<string, string>((sourceVersionId) => repository.createPipelineTemplateVersion(sourceVersionId))
}

export function usePublishPipelineTemplateVersion() {
  return useTemplateMutation<string>((versionId) => repository.publishPipelineTemplateVersion(versionId))
}

export function useSetPipelineTemplateActive() {
  return useTemplateMutation<{ templateId: string; active: boolean }>((input) => repository.setPipelineTemplateActive(input.templateId, input.active))
}

export function useDeletePipelineTemplate() {
  return useTemplateMutation<string>((templateId) => repository.deletePipelineTemplate(templateId))
}

export function usePipelineStageEstimatesQuery(pipelineTemplateId?: string) {
  return useQuery({
    queryKey: queryKeys.pipelineStageEstimates(pipelineTemplateId ?? ""),
    queryFn: () => repository.listPipelineStageEstimates(pipelineTemplateId!),
    enabled: Boolean(pipelineTemplateId),
  })
}

export function useSavePipelineStageEstimates() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: SavePipelineStageEstimatesInput) => repository.savePipelineStageEstimates(input),
    onSuccess: (_, input) => client.invalidateQueries({ queryKey: queryKeys.pipelineStageEstimates(input.pipelineTemplateId) }),
  })
}

export function useImprintsQuery() {
  return useQuery({ queryKey: queryKeys.imprints, queryFn: () => repository.listImprints(), select: sortImprints })
}

function useImprintMutation<T, TResult = void>(mutationFn: (input: T) => Promise<TResult>) {
  const client = useQueryClient()
  return useMutation({
    mutationFn,
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.imprints }),
        client.invalidateQueries({ queryKey: queryKeys.templates }),
      ])
    },
  })
}

export function useCreateImprint() {
  return useImprintMutation<ImprintInput, string>((input) => repository.createImprint(input))
}

export function useUpdateImprint() {
  return useImprintMutation<{ imprintId: string; input: ImprintInput; expectedVersion?: number }>((input) => repository.updateImprint(input.imprintId, input.input, input.expectedVersion))
}

export function useSetImprintActive() {
  return useImprintMutation<{ imprintId: string; active: boolean; reason: string }>((input) =>
    repository.setImprintActive(input.imprintId, input.active, input.reason),
  )
}

export function useDeleteImprint() {
  return useImprintMutation<string>((imprintId) => repository.deleteImprint(imprintId))
}

export function useUploadImprintLogo() {
  return useImprintMutation<UploadImprintLogoInput>((input) => withUploadingToast(input.file.name, () => repository.uploadImprintLogo(input)))
}

export function useDeleteImprintLogo() {
  return useImprintMutation<ImprintLogoTarget>((input) => repository.deleteImprintLogo(input))
}

export function usePrintShopsQuery() {
  return useQuery({ queryKey: queryKeys.printShops, queryFn: () => repository.listPrintShops() })
}

function usePrintShopMutation<T, TResult = void>(mutationFn: (input: T) => Promise<TResult>) {
  const client = useQueryClient()
  return useMutation({
    mutationFn,
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: queryKeys.printShops }),
        client.invalidateQueries({ queryKey: ["product"] }),
      ])
    },
  })
}

export function useCreatePrintShop() {
  return usePrintShopMutation<PrintShopInput, string>((input) => repository.createPrintShop(input))
}

export function useUpdatePrintShop() {
  return usePrintShopMutation<{ printShopId: string; input: PrintShopInput; expectedVersion?: number }>((payload) =>
    repository.updatePrintShop(payload.printShopId, payload.input, payload.expectedVersion),
  )
}

export function useDeletePrintShop() {
  return usePrintShopMutation<string>((printShopId) => repository.deletePrintShop(printShopId))
}

export function useGoogleDriveConnectionQuery() {
  return useQuery({ queryKey: queryKeys.googleDriveConnection, queryFn: () => repository.getGoogleDriveConnection() })
}

export function useStartGoogleDriveConnection() {
  return useMutation({ mutationFn: (returnUrl: string) => repository.startGoogleDriveConnection(returnUrl) })
}

export function useDisconnectGoogleDrive() {
  const client = useQueryClient()
  return useMutation({ mutationFn: () => repository.disconnectGoogleDrive(), onSuccess: () => client.invalidateQueries({ queryKey: queryKeys.googleDriveConnection }) })
}

export function useSyncProductionWithDrive() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: SyncProductionWithDriveInput) => repository.syncProductionWithDrive(input),
    onSuccess: (result) => {
      if (!result.applied) return
      void client.invalidateQueries({ queryKey: ["production-run"] })
      void client.invalidateQueries({ queryKey: ["production-runs"] })
      void client.invalidateQueries({ queryKey: ["dashboard"] })
    },
  })
}

export function useDriveItemStageFoldersQuery(runId: string, itemId: string, enabled: boolean) {
  return useQuery({ queryKey: queryKeys.driveItemStageFolders(runId, itemId), queryFn: () => repository.getDriveItemStageFolders({ runId, itemId }), enabled, staleTime: 5 * 60 * 1000, retry: false })
}

export function useCreateProductDriveFolders() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateProductDriveFoldersInput) => repository.createProductDriveFolders(input),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ["catalog-products"] }),
        client.invalidateQueries({ queryKey: ["catalog-product"] }),
        client.invalidateQueries({ queryKey: ["catalog-item"] }),
        client.invalidateQueries({ queryKey: ["catalog-item-activity"] }),
        client.invalidateQueries({ queryKey: ["drive-item-stage-folders"] }),
        client.invalidateQueries({ queryKey: ["production-run"] }),
        client.invalidateQueries({ queryKey: ["production-runs"] }),
      ])
    },
  })
}

export function useUploadFileToDriveStage() {
  return useMutation({ mutationFn: (input: UploadToDriveStageInput) => withUploadingToast(input.file.name, () => repository.uploadFileToDriveStage(input)) })
}
